import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext.js'
import { ApiError } from '../api/client.js'
import { LANGUAGE_LABELS, setLanguage, type SupportedLanguage } from '../i18n/index.js'

/**
 * Stage 3 §2.1: phone + password, plus a language selector that takes effect
 * before login completes. A company code field is ADDED beyond the original
 * Stage 3 wireframe — see README: phone is unique per tenant, not globally, so
 * login cannot resolve which tenant without it.
 *
 * Two-column branded layout matches the Trustline cross-product standard
 * (see TRecon's login page) — left panel is marketing/brand, right panel is
 * the actual functional form. Only the right panel's field labels go through
 * i18n; the left panel's marketing copy is new and not yet in the catalog.
 */
export function LoginPage() {
  const { t, i18n } = useTranslation()
  const { login } = useAuth()
  const [tenantCode, setTenantCode] = useState('')
  const [phone, setPhone] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login(tenantCode.trim(), phone.trim(), password)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Unable to sign in')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="tf-auth-shell">
      <aside className="tf-auth-panel">
        <div className="tf-auth-wordmark">
          <span className="tf-auth-t">T</span>
          <span className="tf-auth-product">Fleet</span>
        </div>
        <div className="tf-auth-divider" />
        <span className="tf-auth-category">Transport Management Software</span>

        <div className="tf-auth-icon-box">
          <TruckIcon />
        </div>

        <h1 className="tf-auth-tagline">
          Transport management built for how Indian fleets actually run.
        </h1>

        <ul className="tf-auth-features">
          <li>
            <CheckIcon />
            <span>FTL and PTL bookings, your own fleet and broker vehicles, side by side</span>
          </li>
          <li>
            <CheckIcon />
            <span>E-Way Bill distance and transit time calculated automatically from pincodes</span>
          </li>
          <li>
            <CheckIcon />
            <span>GST treatment derived straight from the Lorry Receipt — never guessed at invoicing time</span>
          </li>
        </ul>

        <div className="tf-auth-powered">
          <span>POWERED BY</span>
          <img src="/powered-by-trustline.png" alt="Trustline" />
        </div>
      </aside>

      <div className="tf-auth-form-side">
        <div className="tf-auth-form-card">
          <div className="tf-auth-lang">
            <div className="tf-lang" role="group" aria-label={t('common.language')}>
              {(Object.keys(LANGUAGE_LABELS) as SupportedLanguage[]).map((code) => (
                <button
                  key={code}
                  type="button"
                  aria-pressed={i18n.language === code}
                  onClick={() => setLanguage(code)}
                >
                  {LANGUAGE_LABELS[code]}
                </button>
              ))}
            </div>
          </div>

          <h1>{t('login.title')}</h1>

          <form onSubmit={handleSubmit} className="tf-form">
            <label className="tf-field">
              <span>{t('login.companyCode')}</span>
              <input
                value={tenantCode}
                onChange={(e) => setTenantCode(e.target.value)}
                autoComplete="organization"
                placeholder="VESAKHA"
                required
              />
              <small>{t('login.companyCodeHint')}</small>
            </label>

            <label className="tf-field">
              <span>{t('login.phone')}</span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="numeric"
                autoComplete="tel"
                maxLength={10}
                required
              />
            </label>

            <label className="tf-field">
              <span>{t('login.password')}</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>

            {error && <p className="tf-error">{error}</p>}

            <button type="submit" className="tf-button-primary" disabled={submitting}>
              {submitting ? t('common.loading') : t('login.submit')}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}

function TruckIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2dd4bf" strokeWidth="1.6">
      <rect x="1.5" y="7" width="11" height="8" rx="1" />
      <path d="M12.5 10h4l3 3v2h-7z" />
      <circle cx="5.5" cy="17" r="1.6" />
      <circle cx="16" cy="17" r="1.6" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 8.5l3.2 3.2L13 4.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}
